import React from 'react';
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormLabel,
	Stack,
	TextField,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { isAfter, isValid } from 'date-fns';
import { useDispatch } from 'react-redux';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { showToast } from '@/features/toast/toastSlice';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useMsGraph } from '@/hooks/useMsGraph';
import { useRecordInformedConsentMutation } from '@/features/clients/clientsApi';
import { mapUploadedAttachment } from '@/features/informedConsent/helpers';
import { updateConsentCache } from '@/features/informedConsent/consentCache';
import { safelyParseDate } from '@/utils/date';
import FileUpload from '@/components/FileUpload';
import FolioCheckbox from '@/components/FolioCheckbox';

const validationSchema = Yup.object().shape({
	notApplicable: Yup.boolean(),
	dateReceived: Yup.date()
		.nullable()
		.when('notApplicable', {
			is: true,
			then: (schema) => schema.notRequired(),
			otherwise: (schema) =>
				schema
					.required('Date received is required.')
					.test('valid-date', 'Date received is required.', (value) => isValid(value))
					.test(
						'not-future',
						'Date received cannot be in the future.',
						(value) => !value || !isAfter(value, new Date()),
					),
		}),
	notes: Yup.string(),
	files: Yup.array(),
});

export const RecordInformedConsentForm = () => {
	const dispatch = useDispatch();
	const { clientId, clientRow, dealRow } = useObjectContext();
	const { uploadFilesAndRetry } = useMsGraph();
	const [recordInformedConsent] = useRecordInformedConsentMutation();

	const handleClose = (resetForm) => {
		resetForm?.();
		dispatch(closeDialog('recordInformedConsent'));
	};

	return (
		<Formik
			initialValues={{
				notApplicable: false,
				dateReceived: new Date(),
				notes: '',
				files: [],
			}}
			validationSchema={validationSchema}
			onSubmit={async (values, { setSubmitting, resetForm }) => {
				try {
					let attachments = [];

					if (values.files?.length > 0) {
						const uploadRes = await uploadFilesAndRetry({
							driveId:
								dealRow?.clientFolderDriveId ?? clientRow?.clientFolderDriveId,
							itemId: dealRow?.notesDriveId ?? clientRow?.notesDriveId,
							files: values.files,
							silent: true,
						});
						attachments = (uploadRes?.uploadedFiles ?? []).map(mapUploadedAttachment);

						if (uploadRes?.failedCount > 0) {
							dispatch(
								showToast({
									message:
										'Some attachments could not be uploaded. Please try again.',
									error: true,
								}),
							);
							return;
						}
					}

					await recordInformedConsent({
						clientId,
						dealId: dealRow?.id ?? null,
						brokerGroupId: clientRow?.brokerGroupId,
						dateReceived: safelyParseDate(values.dateReceived),
						notes: values.notes,
						attachments,
						notApplicable: Boolean(values.notApplicable),
					}).unwrap();
					await dispatch(updateConsentCache(clientId));

					dispatch(
						showToast({
							message: 'Informed consent recorded',
							success: true,
							duration: 5000,
							autohide: true,
						}),
					);
					handleClose(resetForm);
				} catch (error) {
					console.error('[RecordInformedConsentDialog]', error);
					dispatch(
						showToast({
							message:
								error?.data ??
								error?.message ??
								'Failed to record informed consent. Please try again.',
							error: true,
						}),
					);
				} finally {
					setSubmitting(false);
				}
			}}
		>
			{({ values, setFieldValue, handleChange, isSubmitting, resetForm }) => (
				<Dialog
					open
					onClose={isSubmitting ? undefined : () => handleClose(resetForm)}
					maxWidth='sm'
					fullWidth
				>
					<Form>
						<DialogTitle sx={{ fontWeight: 500, pb: 1 }}>
							Record Informed Consent
						</DialogTitle>
						<DialogContent>
							<Stack spacing={2.5} sx={{ mt: 1 }}>
								<FolioCheckbox
									checked={values.notApplicable}
									disabled={isSubmitting}
									onChange={(e) =>
										setFieldValue('notApplicable', e.target.checked)
									}
									label='Not Applicable'
								/>
								<FormControl fullWidth>
									<FormLabel sx={{ fontWeight: 600 }}>Date received</FormLabel>
									<LocalizationProvider dateAdapter={AdapterDateFns}>
										<DatePicker
											name='dateReceived'
											disableFuture
											disabled={isSubmitting || values.notApplicable}
											format='dd/MM/yyyy'
											value={values.dateReceived}
											sx={{ width: '100%' }}
											onChange={(value) => {
												const now = new Date();
												const newDate =
													!isValid(value) || isAfter(value, now)
														? now
														: value;
												setFieldValue('dateReceived', newDate);
											}}
										/>
									</LocalizationProvider>
								</FormControl>
								<FormControl fullWidth>
									<FormLabel sx={{ fontWeight: 600 }}>Comment</FormLabel>
									<TextField
										name='notes'
										value={values.notes}
										onChange={handleChange}
										disabled={isSubmitting}
										multiline
										minRows={3}
										fullWidth
									/>
								</FormControl>
								<FileUpload
									files={values.files}
									setFiles={(files) => setFieldValue('files', files)}
									loading={isSubmitting}
								/>
							</Stack>
						</DialogContent>
						<DialogActions sx={{ px: 3, py: 2 }}>
							<Button
								type='button'
								onClick={() => handleClose(resetForm)}
								disabled={isSubmitting}
								color='primary'
							>
								Cancel
							</Button>
							<Button
								type='submit'
								variant='contained'
								disabled={isSubmitting || !clientId}
							>
								{isSubmitting ? (
									<CircularProgress size='1em' color='inherit' />
								) : (
									'Record Consent'
								)}
							</Button>
						</DialogActions>
					</Form>
				</Dialog>
			)}
		</Formik>
	);
};
